import React, { Fragment } from 'react';
import { observer, inject } from 'mobx-react';

import BoxWrapper from '~/common/components/BoxWrapper';
import Dropdown from '~/common/components/Dropdown';
import CommonButton from '~/common/components/CommonButton';
import NumberSelector from '~/common/components/NumberSelector';
import CardSelector from '~/common/components/CardSelector';

import gotoLogo from '~/common/images/img-logo-goto.svg';
import gotoMeetingLogoLockup from '~/common/images/goto-meeting-lockup.svg'
import gotoTrainingLogoLockup from '~/common/images/goto-training-lockup.svg'
import gotoWebinarLogoLockup from '~/common/images/goto-webinar-lockup.svg'
// OV logo
import ovLogo from '~/common/images/ov-logo.svg';

import Tracking from '~/common/utils/gaTracking';
import { mapEcommerceItems } from '~/common/utils/trackingHelpers';

import OrderSummary from '../OrderSummary';

// Import Stylesheet
import styles from './customize.css';

function useGetLogoFromProductFamilyKey(productFamilyKey) {
    if (productFamilyKey === 'OPENVOICE') return ovLogo;
    if (productFamilyKey === 'G2W') return gotoWebinarLogoLockup;
    if (productFamilyKey === 'G2T') return gotoTrainingLogoLockup;
    return gotoMeetingLogoLockup;
}

@inject('checkoutStore', 'formsStore', 'contentStore', 'modalStore')
@observer
export default class Customize extends React.Component {
    constructor(props) {
        super(props);
        const { checkoutStore } = this.props;
        checkoutStore.autoSave();
        this.tracking = new Tracking();
    }

    componentDidMount() {
        this.tracking.trackBeginCheckout({
            step: 1,
            ecommerce: mapEcommerceItems({ 
                selectedPlan: this.props.checkoutStore.selectedPlan,
                billingPeriod: this.props.formsStore.BillingPeriod,
                currencyCode: this.props.contentStore.contentData.CurrencyCode,
                licenseSelector: this.props.formsStore.LicenseSelector,
                selectedAddons: this.props.checkoutStore.selectedAddons
            })
        });
    }

    render() {
        const { labelContent, planData } = this.props.contentStore;
        const { selectedPlan, startDate, endDate, addons, planId, selectedAddons, isAnnual, monthDate, selectedProduct, brand, yearlyOption } = this.props.checkoutStore;
        const { LicenseSelector, BillingPeriod } = this.props.formsStore;
        const openVoice = selectedPlan.ProductFamilyKey === 'OPENVOICE';
        const productLogo = useGetLogoFromProductFamilyKey(selectedPlan.ProductFamilyKey);

        const compareModal = () => {
            this.props.modalStore.setModalOptions({
                childComponentString: 'compareModal',
                isModalClosable: true,
                modalType: 'xxlarge',
                isModalOpen: true
            });
        };

        // check if we have two plans to switch between
        const editPlan = planData[1];
        const editBilling = selectedPlan.Prices.length < 2;

        return (
            <div className="customize">
                <BoxWrapper modifier={'box-wrapper__wrapper--dropdown'}>
                    <h3>
                        <div className={`${styles.icon}`}>
                            <img src={productLogo} />
                        </div>
                        {selectedProduct} {selectedPlan.Name}
                    </h3>
                    <span
                        className={styles.light}
                    ><b>{labelContent.Includes}:</b> {selectedPlan.Participants} {labelContent.Participants}{false && editPlan &&
                    <a className={styles.compare} onClick={() => compareModal()}>{labelContent.ComparePlans}</a>}</span>
                    {editPlan &&
                        <Dropdown>
                            <CardSelector
                                data={planData} field={'planId'}
                                selected={planId} stacked={true}
                                type={'plan'}
                            />
                        </Dropdown>
                    }
                </BoxWrapper>
                {!openVoice &&
                    <BoxWrapper modifier={'box-wrapper__wrapper--border'}>
                        <h3>
                            {labelContent.NumberOfLicenses}
                        </h3>
                        <NumberSelector
                            label={LicenseSelector > 1 ? labelContent.Organizers : labelContent.Organizer}
                            tooltip={labelContent.OrganizerDescription}
                        />
                    </BoxWrapper>
                }
                <BoxWrapper modifier={'box-wrapper__wrapper--dropdown box-wrapper__wrapper--border'}>
                    <h3>
                        {!openVoice && isAnnual ? labelContent.BilledAnnually : labelContent.BilledMonthly}
                    </h3>
                    <span className={styles.content}>{startDate} - {!openVoice && isAnnual ? endDate : monthDate}</span>
                    {editBilling ?
                        <CardSelector
                            data={selectedPlan.Prices} field={'BillingPeriod'}
                            selected={BillingPeriod}
                            type={'billing'}
                        />
                        :
                        <Dropdown open={true}>
                            <CardSelector
                                data={selectedPlan.Prices} field={'BillingPeriod'}
                                selected={BillingPeriod}
                                type={'billing'}
                            />
                        </Dropdown>
                    }
                </BoxWrapper>
                {addons.length > 0 &&
                <Fragment>
                    <h2>{labelContent.SelectAddons}</h2>
                    {addons.map((addOn, i) => {
                        let addOnLogo;
                        switch (addOn.ProductFamilyKey) {
                            case 'EA':
                            case 'OPENVOICE':
                                addOnLogo = ovLogo;
                                break;
                            case 'G2W':
                                addOnLogo = gotoWebinarLogoLockup;
                                break;
                            case 'G2T':
                                addOnLogo = gotoTrainingLogoLockup;
                                break;
                            case 'G2M':
                                addOnLogo = gotoMeetingLogoLockup;
                                break;
                            default:
                                addOnLogo = gotoLogo;
                                break;
                        }

                        return (<BoxWrapper key={i} modifier={'box-wrapper__wrapper--dropdown box-wrapper__wrapper--border'}>
                            <h3>
                                <div className={styles.icon}>
                                    <img src={addOnLogo} />
                                </div>
                                {addOn.Name}
                            </h3>
                            <Dropdown arrow={true} open={true}>
                                <span
                                    className={styles.subtitle}
                                    dangerouslySetInnerHTML={{ __html: addOn.Description }}
                                />
                                {addOn.IncludesInAddon && <span
                                    className={styles.includes}
                                ><span>{labelContent.Includes}</span>: {addOn.IncludesInAddon}</span>}
                                {addOn.Prices.length > 1 ?
                                    <CardSelector
                                        addon={addOn} data={addOn.Prices}
                                        small={true} type={'billing'}
                                    />
                                    :
                                    <CardSelector
                                        addon={addOn} data={addOn.Prices}
                                        selectedAddons={selectedAddons}
                                        stacked={true} type={'single'}
                                    />
                                }
                            </Dropdown>
                        </BoxWrapper>);
                    })}
                </Fragment>
                }
                <OrderSummary mobile={true} />
                <CommonButton
                    brand={brand}
                    data-button='customize-proceed'
                    modifier='common-button__proceed'
                    onClick={() => this.props.nextSteps()}
                >{labelContent.ProceedToCheckout}</CommonButton>
            </div>
        );
    }
}
